<template>
  <div
    class="insights-widget"
    :class="numberOfInsightsOverview === 1 ? 'h-100' : ''"
  >
    <div
      v-if="!error && insightsInOverview.length"
      class="row"
      :class="numberOfInsightsOverview === 1 ? 'h-100' : ''"
    >
      <div
        v-for="insight in insightsInOverview"
        :key="`${insight.statItemName}-${insight.result}`"
        class="col d-flex"
        :class="
          columnClassForInsight({
            insight,
            numberOfInsights: numberOfInsightsOverview,
            numberOfColumns: numberOfColumns
          })
        "
      >
        <!-- :callback="
            insightsWithCallback.find(
              (i) => i.statItemName === insight.statItemName
            )
              ? callback
              : null
          " -->
        <InsightsAlert
          :insight="insight"
          :callback="null"
          :messages="messages"
          :showMoreInsightsModal="
            numberOfInsightsDetails > 0
              ? {
                  id: `show-more-${area}-insights-modal`,
                  title: $tr(
                    messages,
                    'insights.buttons.showMoreInsights.title'
                  )
                }
              : null
          "
          :shouldHideDetailedInfo="shouldHideDetailedInfo"
          :hasHideButton="true"
        />
      </div>
      <div class="insights-modal-wrapper">
        <PulsModal
          v-if="numberOfInsightsDetails > 0"
          :id="`show-more-${area}-insights-modal`"
          :title="$tr(messages, `insights.title`)"
          size="xl"
          :BootstrapModalClass="bootstrapModal"
        >
          <template #modal-body>
            <div class="row">
              <div
                v-for="insight in insightsInModal"
                :key="`${insight.statItemName}-${insight.result}`"
                class="col d-flex"
                :class="
                  columnClassForInsight({
                    insight,
                    numberOfInsights: numberOfInsightsDetails,
                    numberOfColumns: numberOfColumnsForModal
                  })
                "
              >
                <!-- :callback="
                    insightsWithCallback.find(
                      (i) => i.statItemName === insight.statItemName
                    )
                      ? handleCallbackModal
                      : null
                  " -->
                <InsightsAlert
                  :insight="insight"
                  :area="area"
                  :callback="null"
                  :isDetails="true"
                  :messages="messages"
                  :shouldHideDetailedInfo="shouldHideDetailedInfo"
                />
              </div>
            </div>
          </template>
        </PulsModal>
      </div>
    </div>
  </div>
</template>

<script>
import InsightsAlert from './subcomponents/InsightsAlert.vue'
import commonWidgetProps from '@/components/mixins/common-widget-props'
import { requestTypes } from '@/constants/request-types'
import { errorTypes } from '@/constants/error-types'
import { Modal } from 'bootstrap'
import {
  surveyIsWithinTimePeriod,
  aggregateDates
} from '@/helpers/time-window-helper'

export const insightsWidgetInformation = ({ colData }) => {
  const res = {
    requests: {
      [requestTypes.SURVEY_DETAILS]: {},
      [requestTypes.TAGS]: {}
    }
  }
  if (colData.area === 'team') {
    res.requests[requestTypes.INSIGHTS_TEAM] = {}
  }
  if (colData.area === 'leadership') {
    res.requests[requestTypes.INSIGHTS_LEADERSHIP] = {}
  }
  return res
}

export const insightsWidgetSkeleton = () => {
  return {
    header: {
      height: 2
    },
    body: [
      {
        cols: [{ height: 10 }]
      },
      {
        cols: [{ height: 2 }]
      }
    ]
  }
}

const keepQuestionInsights = (insight) => {
  return insight.statName === 'questions'
}

const InsightsWidget = {
  name: 'InsightsWidget',
  components: {
    InsightsAlert
  },
  mixins: [commonWidgetProps],
  props: {
    callback: {
      type: Function,
      default: () => {}
    },
    insightsTeam: {
      type: Object,
      default: () => {}
    },
    insightsLeadership: {
      type: Object,
      default: () => {}
    },
    data: {
      type: Object,
      default: () => {}
    },
    groupName: {
      type: String,
      default: ''
    },
    theme: {
      type: String,
      default: 'default'
    },
    colSize: {
      type: Number,
      default: 1
    },
    surveyDetails: {
      type: Object,
      default: () => {}
    },
    surveys: {
      type: Array,
      default: () => []
    },
    error: {
      type: Boolean,
      default: false
    },
    tags: {
      // only needed to determine if we have results
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {}
  },
  mounted() {
    if (
      this.selectedAttributeSegment.name !== 'all' ||
      this.insightsInOverview.length === 0 ||
      !this.resultsAvailable ||
      !this.shouldDisplayInsights
    ) {
      this.deregisterWidget({
        id: this.id,
        info: {
          reason: errorTypes.NOT_ENOUGH_DATA,
          hideWidget: true,
          hideWidgetWhenNoSurveyResults: true
        },
        groupName: this.selectedGroupName
      })
    }
  },
  computed: {
    bootstrapModal() {
      return Modal
    },
    callbackFilter() {
      return keepQuestionInsights
    },
    area() {
      return this.data.area
    },
    insightsForArea() {
      return this.area === 'team' ? this.insightsTeam : this.insightsLeadership
    },
    sortedInsights() {
      let insights = []
      if (this.insightsForArea?.insights) {
        const negative = this.sortAndFilterInsights(
          this.insightsForArea.insights.negative
        )
        const positive = this.sortAndFilterInsights(
          this.insightsForArea.insights.positive
        )
        insights = [...negative, ...positive]
      }
      return insights
    },
    messages() {
      return this.insightsForArea?.messages
    },
    insightsInOverview() {
      return this.sortedInsights.slice(0, this.numberOfInsightsOverview)
    },
    insightsInModal() {
      return this.sortedInsights.slice(
        1,
        Math.min(this.numberOfInsightsDetails + 1, this.sortedInsights.length)
      )
    },
    insightsWithCallback() {
      return this.sortedInsights.filter(this.callbackFilter)
    },
    numberOfInsightsDetails() {
      return this.data.numberOfInsightsDetails
    },
    numberOfInsightsOverview() {
      return this.data.numberOfInsightsOverview || 1
    },
    hideStatItemTypes() {
      return this.data.hideStatItemTypes
    },
    shouldHideDetailedInfo() {
      return this.data.shouldHideDetailedInfo
    },
    numberOfColumnsForModal() {
      return this.data.numberOfColumnsForModal || 2
    },
    shouldDisplayInsights() {
      // If a published survey is included in the time window for this aggregate
      // then we should not display Insights widget
      if (this.surveyIsAggregate) {
        const publishedSurveys = this.surveyDetails.allSurveysInfo.filter(
          (s) => s.details.status === 'published'
        )
        if (publishedSurveys.length) {
          const timeWindow = aggregateDates({
            aggregate: this.surveyDetails.details.aggregate
          })
          const publishedSurveysInTimeWindow = publishedSurveys.find((s) => {
            return surveyIsWithinTimePeriod({
              surveyDetails: this.surveyDetails,
              timePeriodStartDate: timeWindow.startDate,
              surveyToCompareWith: s
            })
          })
          return !publishedSurveysInTimeWindow
        }
      }
      return true
    },
    resultsAvailable() {
      const tagNamesWithoutParticipation = Object.keys(this.tags).filter(
        (tagName) => tagName !== 'participation'
      )
      return (
        tagNamesWithoutParticipation.length &&
        tagNamesWithoutParticipation.some((tagName) =>
          Object.keys(this.tags[tagName]).includes('value')
        ) &&
        tagNamesWithoutParticipation.some((tagName) => this.tags[tagName].value)
      )
    },
    numberOfColumns() {
      return this.colSize >= 6 ? 2 : 1
    }
  },
  methods: {
    sortAndFilterInsights(insights) {
      return insights
        .sort((a, b) => a.staticPriority - b.staticPriority)
        .filter((item) => {
          const hideConfigForType =
            this.hideStatItemTypes && this.hideStatItemTypes[item.statName]
          if (!hideConfigForType) return true

          if (!hideConfigForType.statItemNames) {
            if (hideConfigForType.results && hideConfigForType.results.length) {
              return !hideConfigForType.results.includes(item.result)
            }
            return true
          } else if (
            hideConfigForType.statItemNames.length === 0 ||
            hideConfigForType.statItemNames.includes(item.statItemName)
          ) {
            if (hideConfigForType.results && hideConfigForType.results.length) {
              return !hideConfigForType.results.includes(item.result)
            }
            return false
          }
          return true
        })
    },
    openQuestionDetailsForInsight(insight) {
      // TODO: figure out how to navigate to questions
      const categories = this.$store.getters.getCategoryDetails
      const categoryNameForInsight = Object.keys(categories).find(
        (categoryName) =>
          Object.keys(categories[categoryName].questions).includes(
            insight.statItemName
          )
      )

      this.$router.push({
        name: 'ResultDetailsForGroupAndCategory',
        params: {
          questionName: insight.statItemName,
          groupName: this.selectedGroupName,
          categoryName: categoryNameForInsight
        }
      })
    },
    keepQuestionInsights(insight) {
      return insight.statName === 'questions'
    },
    columnClassForInsight({ insight, numberOfInsights, numberOfColumns }) {
      const rest = numberOfInsights % numberOfColumns
      const restThreshold = numberOfInsights - rest

      let numberOfColumnsForThisInsight =
        rest > 0 && insight.staticPriority > restThreshold
          ? rest
          : numberOfColumns
      switch (numberOfColumnsForThisInsight) {
        case 1:
          return 'col-12'
        case 2:
          return 'col-12 col-lg-6'
        case 3:
          return 'col-12 col-lg-4'
        case 4:
          return 'col-12 col-lg-3'
        default:
          return 'col-12'
      }
    },
    handleCallbackModal(insight) {
      const event = new Event(
        `hide.puls.modal#show-more-${this.area}-insights-modal`,
        {
          bubbles: true
        }
      )
      document.dispatchEvent(event)
      this.callback(insight)
    }
  }
}

export default InsightsWidget
</script>
<style scoped>
.insights-modal-wrapper {
  position: absolute;
}
</style>
